import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { MultiOptionInput } from "components/multi-option-input/MultiOptionInput";
import { shortPickerStyles } from "routes/automations/shared/styles";
import {
  IRuleTriggerNewAgentMessage,
  IRuleTriggerNewMessage,
  IRuleTriggerSubject,
} from "store/entities/rules/interfaces";

import { changeTriggerValues } from "../utils";

interface IProps {
  input:
    | FieldInputProps<IRuleTriggerSubject>
    | FieldInputProps<IRuleTriggerNewMessage>
    | FieldInputProps<IRuleTriggerNewAgentMessage>;
}

export const MessageTrigger: FC<IProps> = ({ input }) => {
  const { containsIn, containsInCI, containsWholePhraseIn, containsWholePhraseInCI } = input.value;

  const equalityOptions = [createEqualityOption("contains"), createEqualityOption("doesNotContain")];

  const selectedEqualityOption = [createEqualityOption(input.value.invert ? "doesNotContain" : "contains")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    changeTriggerValues(input, {
      ...input.value,
      invert: selectedOption.key === "doesNotContain",
    });
  };

  const matchingMethodOptions = [
    createMatchingMethodOption("containsWholePhraseIn"),
    createMatchingMethodOption("containsWholePhraseInCI"),
    createMatchingMethodOption("containsIn"),
    createMatchingMethodOption("containsInCI"),
  ];

  const getMatchingMethodKey = ():
    | "containsIn"
    | "containsInCI"
    | "containsWholePhraseIn"
    | "containsWholePhraseInCI" => {
    if (input.value.containsWholePhraseIn) {
      return "containsWholePhraseIn";
    }

    if (input.value.containsWholePhraseInCI) {
      return "containsWholePhraseInCI";
    }

    if (input.value.containsInCI) {
      return "containsInCI";
    }

    return "containsIn";
  };

  const selectedMatchingMethodOption = [createMatchingMethodOption(getMatchingMethodKey())];

  const handleSelectMatchingMethodOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    changeTriggerValues(
      input,
      {
        invert: input.value.invert,
        [selectedOption.key]: selectedPhrases,
      },
      { overwrite: true }
    );
  };

  const selectedPhrases = containsIn || containsInCI || containsWholePhraseIn || containsWholePhraseInCI || [];

  const handleAddPhrase = (phrase: string): void => {
    changeTriggerValues(input, {
      [getMatchingMethodKey()]: [...selectedPhrases, phrase],
    });
  };

  const handleRemovePhrase = (phrase: string): void => {
    changeTriggerValues(input, {
      [getMatchingMethodKey()]: selectedPhrases.filter(p => p !== phrase),
    });
  };

  const mustEnterPhrase = ["containsWholePhraseIn", "containsWholePhraseInCI"].includes(getMatchingMethodKey());

  const validateInput = (phrase: string): string | null => {
    if (selectedPhrases.includes(phrase)) {
      return mustEnterPhrase ? "Phrase already exists" : "String of characters already entered";
    }

    return null;
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOption}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        options={matchingMethodOptions}
        selected={selectedMatchingMethodOption}
        onSelect={handleSelectMatchingMethodOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <MultiOptionInput
        placeholder={mustEnterPhrase ? "Enter phrase and confirm it" : "Enter string of characters and confirm it"}
        selectedOptions={selectedPhrases}
        onOptionRemove={handleRemovePhrase}
        onOptionAdd={handleAddPhrase}
        validateInput={validateInput}
      />
    </>
  );
};

const getMatchingMethodText = (
  key: "containsIn" | "containsInCI" | "containsWholePhraseIn" | "containsWholePhraseInCI"
): string | null => {
  switch (key) {
    case "containsWholePhraseIn":
      return "phrase (case-sensitive)";
    case "containsWholePhraseInCI":
      return "phrase";
    case "containsInCI":
      return "string of characters";
    case "containsIn":
      return "string of characters (case-sensitive)";
    default:
      return null;
  }
};

const createEqualityOption = (key: "contains" | "doesNotContain"): IPickerListItem => ({
  key,
  name: key === "contains" ? "contains" : "does not contain",
});

const createMatchingMethodOption = (
  key: "containsIn" | "containsInCI" | "containsWholePhraseIn" | "containsWholePhraseInCI"
): IPickerListItem => ({
  key,
  name: getMatchingMethodText(key) ?? "",
});
